import React, { useEffect, useMemo, useState } from "react";
import BaseModal from "../BaseModal";
import {
  CERTAINTY_LABELS,
  DEFAULT_EDUCATION_CHOICES,
  DEFAULT_GRADE_CHOICES,
  DEFAULT_ONBOARDING_QUESTIONS,
  STEP_UI_BY_KEY,
} from "./onboardingDefaults";
import "./ClassLinkWelcomeModal.scss";

const buildInitialFormState = (questions, initialValues = {}) => {
  const state = {
    is_read_terms_and_policies: Boolean(initialValues.is_read_terms_and_policies),
    allow_data_sharing: Boolean(initialValues.allow_data_sharing),
  };
  for (const question of questions) {
    if (question.input_type === "terms_block") {
      continue;
    }
    const saved = initialValues[question.key];
    if (question.input_type === "checkbox") {
      state[question.key] = Boolean(saved);
    } else if (question.key === "certainty") {
      state[question.key] = saved === undefined || saved === null || saved === "" ? 5 : Number(saved);
    } else if (question.key === "age") {
      state[question.key] = saved === undefined || saved === null || saved === "" ? "" : Number(saved);
    } else {
      state[question.key] = saved ?? "";
    }
  }
  return state;
};

const isFieldComplete = (question, value, initialValues = {}) => {
  if (question.input_type === "terms_block") {
    return Boolean(initialValues.is_read_terms_and_policies);
  }
  if (question.input_type === "checkbox") {
    return question.required ? Boolean(value) : true;
  }
  if (question.key === "age") {
    const age = Number(value);
    return Number.isFinite(age) && age >= 13;
  }
  if (question.key === "grade" || question.input_type === "grade_select") {
    return value !== "" && value !== undefined && value !== null;
  }
  if (question.key === "certainty") {
    const n = Number(value);
    return Number.isFinite(n) && n >= 1 && n <= 10;
  }
  if (question.input_type === "dropdown") {
    return value !== "" && value !== undefined && value !== null;
  }
  return String(value ?? "").trim().length > 0;
};

export const getResumeStepIndex = (questions, initialValues) => {
  const steps = questions?.length ? questions : DEFAULT_ONBOARDING_QUESTIONS;
  if (!steps.length) {
    return 0;
  }
  for (let index = 0; index < steps.length; index += 1) {
    const question = steps[index];
    const value =
      question.input_type === "terms_block"
        ? initialValues.is_read_terms_and_policies
        : initialValues[question.key];
    if (question.required && !isFieldComplete(question, value, initialValues)) {
      return index;
    }
  }
  for (let index = 0; index < steps.length; index += 1) {
    const question = steps[index];
    if (!question.required && question.input_type === "text") {
      if (!String(initialValues[question.key] ?? "").trim()) {
        return index;
      }
    }
  }
  return Math.max(0, steps.length - 1);
};

const ClassLinkWelcomeModal = ({
  isOpen,
  onClose,
  onComplete,
  onDraftSave,
  onExplore,
  isSubmitting = false,
  submitError = null,
  userName = "Alex",
  title,
  subtitle = "Just a few quick questions before you explore",
  successTitle,
  successBody = "Your profile is ready. Time to explore careers, get matched, and figure out what's next.",
  continueText = "Continue",
  finishText = "Finish",
  exploreText = "Explore Career Scoops",
  skipText = "Skip",
  initialValues = {},
  initialStep = 0,
  questions: questionsProp,
  educationChoices: educationChoicesProp,
  gradeChoices: gradeChoicesProp,
  privacyPolicyUrl = "https://career-scoops.com/privacypolicy",
  termsUrl = "https://career-scoops.com/terms",
}) => {
  const questions = useMemo(
    () =>
      questionsProp?.length ? questionsProp : DEFAULT_ONBOARDING_QUESTIONS,
    [questionsProp]
  );
  const educationChoices = useMemo(
    () =>
      educationChoicesProp?.length
        ? educationChoicesProp
        : DEFAULT_EDUCATION_CHOICES,
    [educationChoicesProp]
  );
  const gradeChoices = useMemo(
    () => (gradeChoicesProp?.length ? gradeChoicesProp : DEFAULT_GRADE_CHOICES),
    [gradeChoicesProp]
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [formState, setFormState] = useState(() =>
    buildInitialFormState(questions, initialValues)
  );
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      return;
    }
    setCurrentStep(initialStep);
    setErrors({});
    setShowSuccess(false);
    setFormState(buildInitialFormState(questions, initialValues));
  }, [initialValues, initialStep, isOpen, questions]);

  const headingTitle = title ?? `Hey ${userName}, let's get you set up 👋`;
  const resolvedSuccessTitle = successTitle ?? `You're all set, ${userName}!`;
  const activeQuestion = questions[currentStep];
  const stepUi =
    activeQuestion?.input_type === "terms_block"
      ? STEP_UI_BY_KEY.terms_block
      : activeQuestion
        ? STEP_UI_BY_KEY[activeQuestion.key] || {}
        : {};
  const isLastStep = currentStep === questions.length - 1;

  const setField = (key, value) => {
    setFormState((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: "" }));
  };

  const validateCurrentStep = () => {
    if (!activeQuestion) {
      return true;
    }
    if (activeQuestion.input_type === "terms_block") {
      if (!formState.is_read_terms_and_policies) {
        setErrors((previous) => ({
          ...previous,
          is_read_terms_and_policies: "You must agree to the terms to continue.",
        }));
        return false;
      }
      return true;
    }
    const value = formState[activeQuestion.key];
    if (!activeQuestion.required) {
      return true;
    }
    if (!isFieldComplete(activeQuestion, value, formState)) {
      if (activeQuestion.key === "age") {
        setErrors((previous) => ({
          ...previous,
          age: "You must be 13 or older to continue.",
        }));
      } else if (activeQuestion.key === "grade") {
        setErrors((previous) => ({
          ...previous,
          grade: "Please select your grade to continue.",
        }));
      } else {
        setErrors((previous) => ({
          ...previous,
          [activeQuestion.key]: "This field is required.",
        }));
      }
      return false;
    }
    return true;
  };

  const goToNextStep = (skipValidation = false) => {
    if (!skipValidation && !validateCurrentStep()) {
      return;
    }
    if (isLastStep) {
      onComplete?.(formState);
      return;
    }
    setCurrentStep((previous) => previous + 1);
  };

  const handleExplore = () => {
    onExplore?.(formState);
  };

  const renderStepContent = () => {
    if (!activeQuestion) {
      return null;
    }
    const key = activeQuestion.key;
    const error =
      activeQuestion.input_type === "terms_block"
        ? errors.is_read_terms_and_policies
        : errors[key];

    if (activeQuestion.input_type === "number" && key === "age") {
      const displayAge = formState.age === "" ? 13 : formState.age;
      return (
        <>
          <div className="class-link-welcome-modal__age-row">
            {displayAge > 13 ? (
              <button
                className="class-link-welcome-modal__age-btn"
                onClick={() => setField("age", Math.max(13, Number(displayAge) - 1))}
                type="button"
              >
                -
              </button>
            ) : (
              <div style={{ width: "44px", height: "44px" }} />
            )}
            <div className="class-link-welcome-modal__age-value">{displayAge}</div>
            <button
              className="class-link-welcome-modal__age-btn"
              onClick={() => setField("age", Math.min(99, Number(displayAge) + 1))}
              type="button"
            >
              +
            </button>
          </div>
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    if (activeQuestion.input_type === "text") {
      const placeholder =
        key === "careers" ? "doctor, game designer..." : "mint chip, cookie dough...";
      return (
        <>
          <input
            className="class-link-welcome-modal__input"
            onChange={(event) => setField(key, event.target.value)}
            placeholder={placeholder}
            type="text"
            value={formState[key]}
          />
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    if (activeQuestion.input_type === "scale_1_10") {
      const certainty = Number(formState.certainty) || 5;
      return (
        <div className="class-link-welcome-modal__certainty">
          <div className="class-link-welcome-modal__certainty-value">{certainty}</div>
          <div className="class-link-welcome-modal__certainty-label">
            {CERTAINTY_LABELS[certainty]}
          </div>
          <input
            className="class-link-welcome-modal__range"
            max="10"
            min="1"
            onChange={(event) => setField("certainty", Number(event.target.value))}
            step="1"
            type="range"
            value={certainty}
          />
          <div className="class-link-welcome-modal__range-ends">
            <span>Not sure at all</span>
            <span>100% certain</span>
          </div>
        </div>
      );
    }

    if (activeQuestion.input_type === "grade_select") {
      return (
        <>
          <select
            className="class-link-welcome-modal__select"
            onChange={(event) => setField("grade", event.target.value)}
            value={formState.grade}
          >
            <option value="">Select your grade</option>
            {gradeChoices.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    if (activeQuestion.input_type === "dropdown") {
      return (
        <>
          <select
            className="class-link-welcome-modal__select"
            onChange={(event) => setField("education", event.target.value)}
            value={formState.education}
          >
            <option value="">Select an option</option>
            {educationChoices.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    if (activeQuestion.input_type === "terms_block") {
      return (
        <>
          <div className="class-link-welcome-modal__checkbox-group">
            <label className="class-link-welcome-modal__checkbox-row class-link-welcome-modal__checkbox-row--required">
              <input
                checked={Boolean(formState.is_read_terms_and_policies)}
                className="checkbox-input"
                onChange={(event) => setField("is_read_terms_and_policies", event.target.checked)}
                type="checkbox"
              />
              <span className="class-link-welcome-modal__checkbox-label">
                I certify I'm 13 or older and agree to the{" "}
                <a href={privacyPolicyUrl} rel="noreferrer" target="_blank">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href={termsUrl} rel="noreferrer" target="_blank">
                  Terms of Service
                </a>
                .*
              </span>
            </label>
            <label className="class-link-welcome-modal__checkbox-row">
              <input
                checked={Boolean(formState.allow_data_sharing)}
                className="checkbox-input"
                onChange={(event) => setField("allow_data_sharing", event.target.checked)}
                type="checkbox"
              />
              <span className="class-link-welcome-modal__checkbox-label">
                I allow Career Scoops Inc. to share my data with third-party educational
                institutions as defined in the Privacy Policy.{" "}
                <span className="class-link-welcome-modal__checkbox-optional">(optional)</span>
              </span>
            </label>
          </div>
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    if (activeQuestion.input_type === "checkbox") {
      const isTerms = key === "is_read_terms_and_policies";
      return (
        <>
          <div className="class-link-welcome-modal__checkbox-group">
            <label
              className={`class-link-welcome-modal__checkbox-row${
                activeQuestion.required ? " class-link-welcome-modal__checkbox-row--required" : ""
              }`}
            >
              <input
                checked={Boolean(formState[key])}
                className="checkbox-input"
                onChange={(event) => setField(key, event.target.checked)}
                type="checkbox"
              />
              <span className="class-link-welcome-modal__checkbox-label">
                {isTerms ? (
                  <>
                    I certify I'm 13 or older and agree to the{" "}
                    <a href={privacyPolicyUrl} rel="noreferrer" target="_blank">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href={termsUrl} rel="noreferrer" target="_blank">
                      Terms of Service
                    </a>
                    .*
                  </>
                ) : (
                  <>
                    {activeQuestion.question}
                    {!activeQuestion.required ? (
                      <span className="class-link-welcome-modal__checkbox-optional">
                        {" "}
                        (optional)
                      </span>
                    ) : null}
                  </>
                )}
              </span>
            </label>
          </div>
          {error ? <div className="class-link-welcome-modal__error">{error}</div> : null}
        </>
      );
    }

    return null;
  };

  const stepTitle =
    activeQuestion?.input_type === "terms_block"
      ? "One last thing"
      : activeQuestion?.key === "grade"
        ? "What grade are you in?"
        : activeQuestion?.key === "careers"
          ? "What careers are you thinking about?"
          : activeQuestion?.key === "education"
            ? "What's the highest education you're aiming for?"
            : activeQuestion?.key === "certainty"
              ? "How certain are you about your future career path?"
              : activeQuestion?.key === "favorite_ice_cream_flavor"
                ? "What's your favorite ice cream flavor?"
                : activeQuestion?.question;

  return (
    <BaseModal
      className="class-link-welcome-modal"
      isOpen={isOpen}
      onClose={() => {}}
      showClose={false}
    >
      <div className="class-link-welcome-modal__header">
        {!showSuccess ? (
          <>
            <div className="class-link-welcome-modal__greeting">Welcome to Career Scoops</div>
            <h2 className="class-link-welcome-modal__title">{headingTitle}</h2>
            <p className="class-link-welcome-modal__subtitle">{subtitle}</p>
            <div className="class-link-welcome-modal__dots">
              {questions.map((question, index) => (
                <div
                  className={`class-link-welcome-modal__dot${
                    index === currentStep
                      ? " class-link-welcome-modal__dot--active"
                      : index < currentStep
                        ? " class-link-welcome-modal__dot--done"
                        : ""
                  }`}
                  key={question.key}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {!showSuccess ? (
        <>
          <div className="class-link-welcome-modal__step">
            <div className="class-link-welcome-modal__illustration" aria-hidden="true">
              {stepUi.emoji || "✨"}
            </div>
            <div className="class-link-welcome-modal__question">{stepTitle}</div>
            <div className="class-link-welcome-modal__hint">{stepUi.hint || ""}</div>
            {renderStepContent()}
          </div>

          <div className="class-link-welcome-modal__footer">
            {currentStep > 0 ? (
              <button
                className="class-link-welcome-modal__back-btn"
                onClick={() => setCurrentStep((previous) => previous - 1)}
                type="button"
              >
                Back
              </button>
            ) : null}

            <button
              className="class-link-welcome-modal__next-btn"
              disabled={isSubmitting}
              onClick={() => goToNextStep(false)}
              type="button"
            >
              {isSubmitting ? "Saving..." : isLastStep ? finishText : continueText}
            </button>

            {activeQuestion && !activeQuestion.required ? (
              <button
                className="class-link-welcome-modal__skip-btn"
                onClick={() => goToNextStep(true)}
                type="button"
              >
                {skipText}
              </button>
            ) : null}

            {submitError ? (
              <div className="class-link-welcome-modal__error">
                {typeof submitError === "string"
                  ? submitError
                  : submitError?.detail ||
                    submitError?.complete ||
                    "We couldn't save your onboarding answers. Please try again."}
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div className="class-link-welcome-modal__done">
          <div className="class-link-welcome-modal__done-illustration" aria-hidden="true">
            🎉
          </div>
          <div className="class-link-welcome-modal__done-title">{resolvedSuccessTitle}</div>
          <div className="class-link-welcome-modal__done-body">{successBody}</div>
          <button
            className="class-link-welcome-modal__done-btn"
            disabled={isSubmitting}
            onClick={handleExplore}
            type="button"
          >
            {exploreText}
          </button>
        </div>
      )}
    </BaseModal>
  );
};

export default ClassLinkWelcomeModal;
